import { QUESTION_TYPES } from '@api/instant_survey';

import {
  ALL_OTHERS_LABEL,
  ALL_OTHERS_VALUE,
  CUT_ELIGIBLE_QUESTION_TYPES,
  DEFAULT_RANK_TOP_N,
  GROUPED_DEMOGRAPHIC_PRESETS,
  OVERALL_SOURCE_ID,
  QUOTA_GROUP_SOURCE_ID,
  RANK_QUESTION_TYPES,
  ROW_ELIGIBLE_QUESTION_TYPES,
  SOURCE_CATEGORIES,
  VALUE_PICKER_THRESHOLD,
} from './constants';

let idCounter = 0;
export const createId = (prefix = 'ct') => {
  idCounter += 1;
  return `${prefix}-${idCounter}`;
};

export const getQuestionLabel = ({ index, title }) => `Q${index + 1}. ${title}`;

/** Instructional-text entries are dropped and don't advance the question number. */
const getNumberedQuestions = (surveyQuestions = []) =>
  surveyQuestions
    .filter(({ type }) => type !== QUESTION_TYPES.INSTRUCTIONS.value)
    .map((question, index) => ({ ...question, index }));

export const getEligibleQuestions = (surveyQuestions = []) =>
  getNumberedQuestions(surveyQuestions).filter(({ type }) =>
    ROW_ELIGIBLE_QUESTION_TYPES.includes(type)
  );

export const getIneligibleQuestions = (surveyQuestions = []) =>
  getNumberedQuestions(surveyQuestions).filter(
    ({ type }) => !ROW_ELIGIBLE_QUESTION_TYPES.includes(type)
  );

/** Grid questions keep their shared answer scale in `options`, separate from `subQuestions`. */
export const getQuestionOptionValues = (question = {}) =>
  (question.options ?? []).map(({ id, title }) => ({
    id: String(id),
    label: title,
  }));

export const isRankQuestion = ({ type } = {}) => RANK_QUESTION_TYPES.includes(type);

export const getDefaultRankTopN = (question = {}) =>
  Math.min(DEFAULT_RANK_TOP_N, Math.max(getQuestionOptionValues(question).length, 1));

/**
 * Sorts values by instance count, descending. Values without counts keep
 * their relative order after the counted ones; sources with no counts at
 * all are left untouched.
 */
export const sortValuesByCountDesc = (values = []) => {
  if (!values.some(({ count }) => typeof count === 'number')) return values;
  return [...values].sort((a, b) => (b.count ?? -1) - (a.count ?? -1));
};

const buildGroupedPresetSource = (demographicId, demographicValues = []) => {
  const preset = GROUPED_DEMOGRAPHIC_PRESETS[demographicId];
  if (!preset) return null;

  const availableIds = new Set(demographicValues.map(({ id }) => id));
  const bins = preset.bins.filter(({ values }) => values.some((value) => availableIds.has(value)));
  if (!bins.length) return null;

  return {
    category: SOURCE_CATEGORIES.DEMOGRAPHIC,
    id: `${demographicId}__grouped`,
    isGroupedPreset: true,
    label: preset.label,
    presetBins: bins,
    sourceAttribute: demographicId,
    values: [],
  };
};

const OVERALL_SOURCE = {
  category: SOURCE_CATEGORIES.OVERALL,
  id: OVERALL_SOURCE_ID,
  label: 'Overall',
  sourceAttribute: OVERALL_SOURCE_ID,
  values: [{ id: 'total', label: 'Total' }],
};

const buildQuotaGroupSource = (values) => ({
  category: SOURCE_CATEGORIES.QUOTA_GROUP,
  id: QUOTA_GROUP_SOURCE_ID,
  label: 'Quota Group',
  sourceAttribute: QUOTA_GROUP_SOURCE_ID,
  values,
});

/** Question cuts in programmed order; `getOptionCounts` returns `{ [optionId]: count }` or `{}`. */
const buildQuestionSources = (surveyQuestions, getOptionCounts) =>
  getEligibleQuestions(surveyQuestions)
    .filter(({ type }) => CUT_ELIGIBLE_QUESTION_TYPES.includes(type))
    .flatMap((question) => {
      const optionCounts = getOptionCounts(question);
      const values = getQuestionOptionValues(question).map((value) =>
        optionCounts[value.id] === undefined ? value : { ...value, count: optionCounts[value.id] }
      );
      if (!values.length) return [];

      return [
        {
          category: SOURCE_CATEGORIES.QUESTION,
          id: `question__${question.id}`,
          isMultiSelectCut: question.type === 'CHECKBOX',
          label: getQuestionLabel(question),
          sourceAttribute: String(question.id),
          values,
        },
      ];
    });

/** Facet categories emitted by `buildGlobalFilterFacetsFromCatalog`. */
const FACET_CATEGORIES = {
  DEMOGRAPHIC: 'demographic',
  QUESTION: 'question',
  QUOTA_GROUP: 'quota',
  TRIP_ATTRIBUTE: 'trip',
};

const getFacetOptionValues = (options = [], withCounts) =>
  options.map(({ count, label, value }) => ({
    id: String(value),
    label: label ?? String(value),
    ...(withCounts && typeof count === 'number' && { count }),
  }));

const getFacetOptionCounts = (facet, withCounts) =>
  withCounts
    ? Object.fromEntries(
        (facet?.options ?? [])
          .filter(({ count }) => typeof count === 'number')
          .map(({ count, value }) => [String(value), count])
      )
    : {};

/**
 * `withCounts` follows the global filters redesign gate so `n=` only appears once the
 * rest of the dashboard shows the same catalog counts.
 * The export endpoint resolves trip attributes by display header, hence `sourceAttribute`
 * is the label, and it cannot resolve the catalog's compare-only Age/Income rollups, so
 * those are skipped in favor of GROUPED_DEMOGRAPHIC_PRESETS on the granular attribute.
 */
export const buildBannerSources = ({
  globalFilterFacets = [],
  surveyQuestions,
  withCounts = false,
} = {}) => {
  const sources = [OVERALL_SOURCE];
  const facetsIn = (category) => globalFilterFacets.filter((facet) => facet.category === category);

  const quotaGroupFacet = facetsIn(FACET_CATEGORIES.QUOTA_GROUP)[0];
  if (quotaGroupFacet?.options?.length > 1) {
    sources.push(buildQuotaGroupSource(getFacetOptionValues(quotaGroupFacet.options, withCounts)));
  }

  facetsIn(FACET_CATEGORIES.DEMOGRAPHIC)
    .filter(({ filterable }) => filterable)
    .forEach((facet) => {
      const values = getFacetOptionValues(facet.options, withCounts);
      if (!values.length) return;

      sources.push({
        category: SOURCE_CATEGORIES.DEMOGRAPHIC,
        id: facet.id,
        label: facet.label,
        sourceAttribute: facet.id,
        values,
      });

      const groupedSource = buildGroupedPresetSource(facet.id, values);
      if (groupedSource) sources.push(groupedSource);
    });

  facetsIn(FACET_CATEGORIES.TRIP_ATTRIBUTE).forEach((facet) => {
    const values = getFacetOptionValues(facet.options, withCounts);
    if (!values.length) return;

    sources.push({
      category: SOURCE_CATEGORIES.TRIP_ATTRIBUTE,
      id: `trip__${facet.id}`,
      label: facet.label,
      sourceAttribute: facet.label,
      values,
    });
  });

  const questionFacetsById = new Map(
    facetsIn(FACET_CATEGORIES.QUESTION).map((facet) => [String(facet.id), facet])
  );
  sources.push(
    ...buildQuestionSources(surveyQuestions, (question) =>
      getFacetOptionCounts(questionFacetsById.get(String(question.id)), withCounts)
    )
  );

  return sources;
};

export const sourceNeedsValuePicker = (source) =>
  !source.isGroupedPreset && source.values.length > VALUE_PICKER_THRESHOLD;

/**
 * Create a banner span from a source. For picker sources, `selectedValues` holds the
 * chosen values; low-cardinality sources drop all their values in as chips directly.
 */
export const createSpanFromSource = (source, { selectedValues, withAllOthers } = {}) => {
  let points;

  if (source.isGroupedPreset) {
    // Preserve preset bins as indivisible net members.
    points = source.presetBins.map((bin) => ({
      atomic: true,
      key: createId('point'),
      label: bin.label,
      values: [...bin.values],
    }));
  } else {
    const values = selectedValues ?? source.values;
    points = values.map(({ id, label }) => ({
      key: createId('point'),
      label,
      values: [id],
    }));
    if (withAllOthers) {
      points.push({
        isAllOthers: true,
        key: createId('point'),
        label: ALL_OTHERS_LABEL,
        values: [ALL_OTHERS_VALUE],
      });
    }
  }

  return {
    category: source.category,
    id: createId('span'),
    isMultiSelectCut: Boolean(source.isMultiSelectCut),
    label: source.label,
    points,
    sourceAttribute: source.sourceAttribute,
    sourceId: source.id,
  };
};

export const createOverallSpan = () => createSpanFromSource(OVERALL_SOURCE);

export const getDefaultNetName = (points = []) => points.map(({ label }) => label).join(' & ');

/**
 * Combine two or more points of a span into a named net. Member points are kept so
 * a net can be dissolved back to them (grouped bins reappear as bins, not raw values).
 */
export const combineSpanPoints = (span, pointKeys, name) => {
  const members = span.points.filter(({ key }) => pointKeys.includes(key));
  // "All Others" is the complement of the span's explicit values, not a value
  // itself - folding it into a net would export its sentinel as a literal value.
  if (members.length < 2 || members.some(({ isAllOthers }) => isAllOthers)) return span;

  const net = {
    isNet: true,
    key: createId('point'),
    label: name?.trim() || getDefaultNetName(members),
    memberPoints: members,
    values: members.flatMap(({ values }) => values),
  };
  const firstIdx = span.points.findIndex(({ key }) => pointKeys.includes(key));
  const remaining = span.points.filter(({ key }) => !pointKeys.includes(key));
  remaining.splice(firstIdx, 0, net);

  return { ...span, points: remaining };
};

export const dissolveSpanPoint = (span, pointKey) => {
  const index = span.points.findIndex(({ key }) => key === pointKey);
  const point = span.points[index];
  if (!point?.isNet || !point.memberPoints?.length) return span;

  const points = [...span.points];
  points.splice(index, 1, ...point.memberPoints);
  return { ...span, points };
};

export const renameSpanPoint = (span, pointKey, name) => ({
  ...span,
  points: span.points.map((point) =>
    point.key === pointKey && name?.trim() ? { ...point, label: name.trim() } : point
  ),
});

export const removeSpanPoint = (span, pointKey) => ({
  ...span,
  points: span.points.filter(({ key }) => key !== pointKey),
});

export const countBannerColumns = (banner = []) =>
  banner.reduce((total, { points }) => total + points.length, 0);

export const buildDefaultQuestionConfigs = (surveyQuestions = []) =>
  getEligibleQuestions(surveyQuestions).map((question) => ({
    included: true,
    nets: [],
    questionId: String(question.id),
    ...(isRankQuestion(question) && { rankTopN: getDefaultRankTopN(question) }),
  }));

const buildPointPayload = (point) => ({
  is_all_others: Boolean(point.isAllOthers),
  label: point.label,
  values: point.isAllOthers ? [] : point.values,
});

export const buildExportPayload = ({
  banner = [],
  baseThreshold,
  confidence,
  filterLogic,
  filterRows = [],
  filterSources = [],
  questionConfigs = [],
}) => ({
  banner: banner.map((span) => ({
    is_multi_select_cut: Boolean(span.isMultiSelectCut),
    label: span.label,
    points: span.points.map(buildPointPayload),
    source_id: span.sourceAttribute,
    source_type: span.category,
  })),
  base_threshold: baseThreshold,
  confidence,
  filters: {
    logic: filterLogic,
    rows: filterRows
      .filter(({ sourceId, values }) => sourceId && values.length)
      // Rows echo the banner-span contract so the backend resolves both the same way.
      .map(({ sourceId, values }) => {
        const source = filterSources.find(({ id }) => id === sourceId);
        return {
          source_id: source?.sourceAttribute ?? sourceId,
          source_type: source?.category ?? null,
          values,
        };
      }),
  },
  questions: questionConfigs.map(({ included, nets, questionId, rankTopN }) => ({
    included,
    nets: nets.map(({ name, optionIds }) => ({
      name,
      option_ids: optionIds,
    })),
    question_id: questionId,
    ...(rankTopN !== undefined && { rank_top_n: rankTopN }),
  })),
});
